import React, { useState, useRef, useEffect } from 'react';
import styles from './CommentsModal.module.css';
import { IoClose, IoChatbubblesOutline, IoHeart, IoHeartOutline, IoSend, IoHourglass } from 'react-icons/io5';
import { Colors } from '../helpers/constants';
import { getUserId, getUser } from '../helpers/userHelpers';
import { uppercaseFirstLetter, formatTimeAgo } from '../helpers/formatHelpers';
import { POST } from '../config/api';
import { showToast } from '../helpers/toast';
import { useNavigate } from 'react-router-dom';

const CommentItem = ({ comment, currentUserId, onLike, onNavigateToUser }) => {
  const [isLiked, setIsLiked] = useState(comment?.likes?.includes(currentUserId));
  const [likesCount, setLikesCount] = useState(comment?.likes?.length || 0);

  const handleLike = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);
    setLikesCount((prev) => (newLikedState ? prev + 1 : prev - 1));
    onLike?.(comment?._id);
  };

  const handleUserPress = () => {
    if (comment.user._id !== currentUserId && onNavigateToUser) {
      onNavigateToUser(comment.user._id);
    }
  };

  return (
    <div className={styles.commentItem}>
      <div className={styles.commentHeader}>
        <div
          className={`${styles.avatarContainer} ${comment.user._id !== currentUserId ? styles.clickable : ''}`}
          onClick={handleUserPress}
        >
          <img
            src={comment.user.pictureURL || '/default-avatar.png'}
            alt={comment.user.username}
            className={styles.avatar}
            onError={(e) => {
              e.target.src = '/default-avatar.png';
            }}
          />
        </div>
        <div className={styles.commentContent}>
          <div className={styles.commentBubble}>
            <div
              className={`${styles.username} ${comment.user._id !== currentUserId ? styles.clickableUsername : ''}`}
              onClick={handleUserPress}
            >
              {comment.user._id === currentUserId ? 'You' : `@${uppercaseFirstLetter(comment.user.username)}`}
            </div>
            <div className={styles.commentText}>{comment.comment}</div>
          </div>
          <div className={styles.commentMeta}>
            <span className={styles.timeText}>{formatTimeAgo(comment.createdAt)}</span>
            {likesCount > 0 && (
              <div className={styles.likesContainer}>
                <IoHeart size={12} color={Colors.DANGER} />
                <span className={styles.likesText}>{likesCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.likeButton} onClick={handleLike}>
        <div className={styles.likeButtonContent}>
          {isLiked ? (
            <IoHeart size={16} color={Colors.DANGER} />
          ) : (
            <IoHeartOutline size={16} color={Colors.DARK_GREY} />
          )}
        </div>
      </div>
    </div>
  );
};

const CommentsModal = ({ visible, onClose, reelId, comments, onAddComment }) => {
  const [localComments, setLocalComments] = useState(comments);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userId = getUserId();
  const currentUser = getUser();
  const commentsEndRef = useRef(null);
  const textInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  useEffect(() => {
    if (visible && textInputRef.current) {
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 300);
    }
  }, [visible]);

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    const { data, error } = await POST(`/reels/${reelId}/comments`, { comment: commentText.trim() });
    if (!error) {
      const newCommentWithUser = {
        ...data.comment,
        user: {
          _id: currentUser.id || currentUser._id,
          username: currentUser.username,
          pictureURL: currentUser.pictureURL,
        },
      };
      const updatedComments = [...localComments, newCommentWithUser];
      setLocalComments(updatedComments);
      setCommentText('');
      onAddComment?.(newCommentWithUser);

      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      showToast('Failed to add comment', '', 'error', { position: 'top-center' });
    }

    setIsSubmitting(false);
  };

  const handleLikeComment = async (commentId) => {
    try {
      const previousComments = [...localComments];

      setLocalComments((prev) =>
        prev.map((comment) => {
          if (comment?._id === commentId) {
            const isLiked = comment?.likes?.includes(userId);
            return {
              ...comment,
              likes: isLiked ? comment?.likes?.filter((id) => id !== userId) : [...comment?.likes, userId],
            };
          }
          return comment;
        })
      );

      const { data, error } = await POST(`/reels/comments/${commentId}/like`, {});

      if (error) {
        setLocalComments(previousComments);
        showToast('Failed to like comment', '', 'error', { position: 'top-center' });
      } else {
        setLocalComments((prev) =>
          prev.map((comment) => {
            if (comment?._id === commentId) {
              return {
                ...comment,
                likes: data.isLiked ? [...comment?.likes, userId] : comment?.likes?.filter((id) => id !== userId),
              };
            }
            return comment;
          })
        );
      }
    } catch (error) {
      showToast('Failed to like comment', '', 'error', { position: 'top-center' });
    }
  };

  const handleNavigateToUser = (userId) => {
    onClose();
    navigate(`/user/${userId}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  if (!visible) return null;

  const sortedComments = [...localComments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Comments</h2>
          <div className={styles.closeButton} onClick={onClose}>
            <IoClose size={24} color={Colors.WHITE} />
          </div>
        </div>

        {/* Comments List */}
        <div className={styles.commentsContainer}>
          {sortedComments.length > 0 ? (
            <>
              {sortedComments.map((comment) => (
                <CommentItem
                  key={comment._id}
                  comment={comment}
                  currentUserId={userId}
                  onLike={handleLikeComment}
                  onNavigateToUser={handleNavigateToUser}
                />
              ))}
              <div ref={commentsEndRef} />
            </>
          ) : (
            <div className={styles.emptyContainer}>
              <IoChatbubblesOutline size={64} color={Colors.DARK_GREY} />
              <p className={styles.emptyText}>No comments yet</p>
              <p className={styles.emptySubtext}>Be the first to share your thoughts!</p>
            </div>
          )}
        </div>

        {/* Input Section */}
        <div className={styles.inputContainer}>
          <div className={styles.inputRow}>
            <div className={styles.textInputContainer}>
              <input
                ref={textInputRef}
                type="text"
                className={styles.textInput}
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={500}
                disabled={isSubmitting}
              />
            </div>
            <button
              className={styles.sendButton}
              onClick={handleSubmitComment}
              disabled={!commentText.trim() || isSubmitting}
              style={{ opacity: commentText.trim() && !isSubmitting ? 1 : 0.5 }}
            >
              {isSubmitting ? <IoHourglass size={20} color={Colors.WHITE} /> : <IoSend size={20} color={Colors.WHITE} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsModal;

